import { useState, type ReactNode } from "react";

import classes from "./VideoFrame.module.css";

export interface VideoFrameProps {
  /** The video's signed URL. */
  src: string;
  /**
   * Names the picture for a screen reader: the file's name. Without one the frame is decoration,
   * hidden from screen readers, as inside a control that names the file itself.
   */
  label?: string;
  /** Shown over the frame while the video cannot be read: the card's placeholder. */
  fallback: ReactNode;
  /** Told of a failed load, once per `src` until it loads (a reader may fetch fresh URLs). */
  onError?: () => void;
  /** Never plays: a frame inside another control (the editor's clip blocks). */
  still?: boolean;
}

/** Where the frame is taken: a moment in, since some browsers paint nothing at 0. */
const FRAME_AT = "#t=0.1";

/**
 * A video's picture where no thumbnail or poster exists yet (the media worker that makes them,
 * S2, is not built): the browser loads only the metadata from the signed URL and shows a frame
 * a tenth of a second in, filling its box like an image tile. A media element needs no CORS. It
 * plays, muted, while the pointer is over it. A video that cannot be read stays mounted in its
 * box, unseen under `fallback` (web/AGENTS.md, #1057): the media cookies' retry asks again for
 * that very element once a renewal lands (spec assets/media-cookies, #944), and its data's
 * arrival takes the fallback away.
 */
export function VideoFrame({ src, label, fallback, onError, still = false }: VideoFrameProps) {
  // Keyed by the URL, so a fresh URL after a failure gets a fresh try.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const covered = failedSrc === src;
  return (
    <span className={classes.box}>
      <video
        className={classes.frame}
        src={`${src}${FRAME_AT}`}
        muted
        playsInline
        preload="metadata"
        data-failed={covered || undefined}
        {...(label === undefined || covered ? { "aria-hidden": true } : { "aria-label": label })}
        onError={() => {
          if (covered) return;
          setFailedSrc(src);
          onError?.();
        }}
        onLoadedData={() => setFailedSrc(null)}
        onMouseEnter={(event) => {
          // Hover is a preview only; a refused play keeps the frame.
          if (!still) event.currentTarget.play()?.catch(() => undefined);
        }}
        onMouseLeave={(event) => {
          if (still) return;
          const video = event.currentTarget;
          video.pause();
          video.currentTime = 0.1;
        }}
      />
      {covered ? <span className={classes.fallback}>{fallback}</span> : null}
    </span>
  );
}
